import { useQuery } from '@tanstack/react-query'
import { Capacitor } from '@capacitor/core'
import { Geolocation } from '@capacitor/geolocation'
import { FlightMapNative } from '@/lib/native/flight-map'

export interface DeviceLocation {
  latitude: number
  longitude: number
  cityName?: string
  timezone?: string
}

export function useCurrentDeviceLocation(enabled: boolean = true) {
  return useQuery({
    queryKey: ['device-location'],
    queryFn: async (): Promise<DeviceLocation> => {
      // On native, ask via the Capacitor permission API first. On web,
      // `requestPermissions` rejects ("not implemented") before the browser is
      // ever asked, so skip straight to `getCurrentPosition` — its web
      // implementation calls `navigator.geolocation`, which triggers the
      // browser's own permission prompt (and rejects if the user denies).
      if (Capacitor.isNativePlatform()) {
        const permission = await Geolocation.checkPermissions()
        if (permission.location !== 'granted' && permission.coarseLocation !== 'granted') {
          const requested = await Geolocation.requestPermissions({ permissions: ['location'] })
          if (requested.location !== 'granted' && requested.coarseLocation !== 'granted') {
            throw new Error('Location permission denied')
          }
        }
      }

      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: false,
        timeout: 10_000,
      })

      const { latitude, longitude } = position.coords

      let cityName: string | undefined
      let timezone: string | undefined
      try {
        const result = await FlightMapNative.reverseGeocode({ lat: latitude, lon: longitude })
        cityName = result.cityName ?? undefined
        timezone = result.timezone ?? undefined
      } catch {
        // Best-effort reverse geocode; ignore failures so the coord still resolves.
      }

      return { latitude, longitude, cityName, timezone }
    },
    enabled,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: false,
    // Location is best-effort: denial/timeout failures are surfaced inline by
    // FlightCard (and the map just omits the pin), so opt out of the global
    // error toast in main.tsx.
    meta: { suppressGlobalErrorToast: true },
  })
}
